import React from 'react';
import { Box, Button, HStack, Spinner, StackDivider, Text, VStack } from '@chakra-ui/react';
import { EmailIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { EventEmail } from '../../types/api';
import SectionCard from '../events/SectionCard';
import { AUTOMATIC_OR_SYSTEM, CustomLastSent, SystemEmailAction, systemLastSentText } from './SendEmailModal';

/**
 * "Envoi emails" tab: one row per email of the event except the automatic RSVP replies
 * (invitation, reminder, final confirmation, every custom email). "Envoyer" hands the row's
 * key ('system:<kind>' | 'custom:<id>') to the page, which opens the send modal on that email.
 */
const SendEmailsTab: React.FC<{
  eventId: number;
  systemEmails: SystemEmailAction[];
  onSend: (pick: string) => void;
  canSend?: boolean;
  /** Draft events cannot send: show a hint (the page also passes canSend=false). */
  isDraft?: boolean;
}> = ({ eventId, systemEmails, onSend, canSend = true, isDraft = false }) => {
  const emailsQ = useQuery({
    queryKey: ['event-emails', eventId],
    queryFn: () => apiService.getEventEmails(eventId),
  });
  const customEmails: EventEmail[] = (emailsQ.data || []).filter((e) => !AUTOMATIC_OR_SYSTEM.includes(e.kind));

  const Row: React.FC<{ rowKey: string; name: string; hint?: string; lastSent?: React.ReactNode; testId: string }> = ({ rowKey, name, hint, lastSent, testId }) => (
    <Box data-testid={'email-row-' + testId}>
        <HStack justify="space-between" spacing={3}>
          <Box minW={0}>
            <Text fontWeight="semibold" fontSize="sm">{name}</Text>
            {lastSent}
            {hint && <Text fontSize="xs" color="gray.600">{hint}</Text>}
          </Box>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<EmailIcon />}
            isDisabled={!canSend}
            onClick={() => onSend(rowKey)}
            data-testid={'send-row-' + testId}
          >
            Envoyer
          </Button>
        </HStack>
    </Box>
  );

  return (
    <VStack align="stretch" spacing={4} data-testid="send-emails-tab">
      {isDraft && <Text fontSize="sm" color="gray.600" data-testid="send-emails-draft-hint">Publiez l'événement pour envoyer des e-mails.</Text>}
      <SectionCard title="E-mails du système" testId="send-emails-system-section">
        <VStack align="stretch" spacing={3} divider={<StackDivider />}>
          {systemEmails.map((s) => (
            <Row key={s.kind} rowKey={'system:' + s.kind} name={s.label} testId={'system-' + s.kind}
                 lastSent={<Text fontSize="xs" color="gray.600" data-testid={'last-sent-' + s.kind}>{systemLastSentText(s)}</Text>}
                 hint={s.countLabel} />
          ))}
        </VStack>
      </SectionCard>
      {(emailsQ.isLoading || customEmails.length > 0) && (
        <SectionCard title="E-mails personnalisés" testId="send-emails-custom-section">
          {emailsQ.isLoading && <Spinner size="sm" />}
          <VStack align="stretch" spacing={3} divider={<StackDivider />}>
            {customEmails.map((e) => (
              <Row key={e.id} rowKey={'custom:' + e.id} name={e.name} testId={'custom-' + e.id}
                   lastSent={<CustomLastSent eventId={eventId} emailId={e.id} />} />
            ))}
          </VStack>
        </SectionCard>
      )}
    </VStack>
  );
};

export default SendEmailsTab;
